import React, { useState, useEffect } from 'react';
import { Link } from 'react-router';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import LeftNavigationBar from 'screens/Main/components/LeftNavigationBar';
import TopNavigationBar from 'screens/Main/components/TopNavigationBar';
import LoginPromptModal from 'components/LoginPromptModal';
import Footer from 'components/Footer';
import { SEOHelmet, BreadcrumbList } from 'components/SEO';
import { copyToClipboard } from 'utils/copyToClipboard';
import './apiDocumentation.css';

// Code block with copy functionality
const CodeBlock: React.FC<{ code: string; label?: string }> = ({
  code,
  label,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    // writeText rejects with NotAllowedError when the clipboard permission is
    // in the `prompt` state, which previously skipped setCopied entirely.
    if (!(await copyToClipboard(code))) return;
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="api-docs-code-block">
      <div className="api-docs-code-header">
        <span className="api-docs-code-label">{label || 'Code'}</span>
        <button
          className={`api-docs-copy-btn ${copied ? 'copied' : ''}`}
          onClick={handleCopy}
        >
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      <div className="api-docs-code-content">
        <pre>{code}</pre>
      </div>
    </div>
  );
};

// Tabbed code examples
const CodeTabs: React.FC<{
  tabs: { label: string; code: string; language: string }[];
}> = ({ tabs }) => {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <div className="api-docs-tabs">
      <div className="api-docs-tab-list">
        {tabs.map((tab, index) => (
          <button
            key={tab.label}
            className={`api-docs-tab ${index === activeTab ? 'active' : ''}`}
            onClick={() => setActiveTab(index)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {tabs.map((tab, index) => (
        <div
          key={tab.label}
          className={`api-docs-tab-content ${index === activeTab ? 'active' : ''}`}
        >
          <CodeBlock code={tab.code} label={tab.language} />
        </div>
      ))}
    </div>
  );
};

const ApiDocumentationPage: React.FC = () => {
  const currentUser = useSelector((state: RootState) => state.auth.user);

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleNavigationAttempt = () => {
    if (!currentUser) {
      setShowLoginModal(true);
    }
  };

  // Code examples for the tabs
  const codeExamples = [
    {
      label: 'React Native',
      language: 'JavaScript',
      code: `async function exportToTradeMagic() {
  try {
    // 1. Get user's collection
    const collection = await getUserCollection();

    // 2. Generate CSV
    const csvData = generateCSV(collection);

    // 3. Encode to base64
    const base64Data = btoa(unescape(encodeURIComponent(csvData)));

    // 4. Create session
    const response = await fetch(
      'https://api.trademagic.gg/api/v1/import-session',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          csvData: base64Data,
          source: 'your-source-id'
        })
      }
    );

    if (!response.ok) {
      throw new Error('Failed to create import session');
    }

    const { importUrl, cardCount, skippedRows } = await response.json();

    // 5. Show confirmation to user
    Alert.alert(
      'Export Ready',
      \`\${cardCount} cards ready to import\${skippedRows > 0 ? \` (\${skippedRows} skipped)\` : ''}\`,
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Open TradeMagic', onPress: () => Linking.openURL(importUrl) }
      ]
    );
  } catch (error) {
    Alert.alert('Export Failed', error.message);
  }
}`,
    },
    {
      label: 'Python',
      language: 'Python',
      code: `import base64
import requests
import webbrowser

def export_to_trademagic(cards):
    # Build CSV
    csv_lines = ["export_type;scryfall_uuid;set_code;quantity;foil_quantity;card_name"]
    for card in cards:
        csv_lines.append(
            f"card;{card['scryfall_uuid']};{card['set_code']};"
            f"{card['quantity']};{card['foil_quantity']};{card['name']}"
        )

    csv_data = "\\n".join(csv_lines)
    csv_base64 = base64.b64encode(csv_data.encode()).decode()

    # Create session
    response = requests.post(
        "https://api.trademagic.gg/api/v1/import-session",
        json={
            "csvData": csv_base64,
            "source": "your-source-id"
        }
    )

    if response.status_code == 200:
        result = response.json()
        print(f"Session created: {result['cardCount']} cards")
        print(f"Skipped: {result['skippedRows']} rows")

        # Open browser for user to complete import
        webbrowser.open(result['importUrl'])
        return result
    else:
        print(f"Error: {response.text}")
        return None`,
    },
    {
      label: 'Node.js',
      language: 'JavaScript',
      code: `const axios = require('axios');

async function exportToTradeMagic(cards) {
  // Build CSV
  const csvLines = ['export_type;scryfall_uuid;set_code;quantity;foil_quantity;card_name'];

  for (const card of cards) {
    csvLines.push(
      \`card;\${card.scryfallUuid};\${card.setCode};\${card.quantity};\${card.foilQuantity};\${card.name}\`
    );
  }

  const csvData = csvLines.join('\\n');
  const csvBase64 = Buffer.from(csvData).toString('base64');

  // Create session
  const response = await axios.post(
    'https://api.trademagic.gg/api/v1/import-session',
    {
      csvData: csvBase64,
      source: 'your-source-id'
    }
  );

  console.log(\`Session created: \${response.data.cardCount} cards\`);
  console.log(\`Import URL: \${response.data.importUrl}\`);

  return response.data;
}`,
    },
  ];

  return (
    <>
      <SEOHelmet
        title="API Documentation - TradeMagic"
        description="Developer documentation for integrating with TradeMagic's import API. Learn how to export collections from your app to TradeMagic."
        canonical="https://trademagic.gg/docs/api"
        type="website"
      />

      <BreadcrumbList
        breadcrumbs={[
          { name: 'Home', url: 'https://trademagic.gg/' },
          {
            name: 'API Documentation',
            url: 'https://trademagic.gg/docs/api',
          },
        ]}
      />

      <div className="api-docs-layout">
        {isMobile && isMobileMenuOpen && (
          <div
            className="api-docs-mobile-overlay"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-hidden="true"
          />
        )}

        <div className="api-docs-content-wrapper">
          <LeftNavigationBar
            currentTab={{ path: '/docs/api', Component: <></> }}
            isPublicMode={!currentUser}
            onNavigationAttempt={handleNavigationAttempt}
            isAuthenticated={!!currentUser}
            isMobile={isMobile}
            isMobileMenuOpen={isMobileMenuOpen}
            onMenuClose={() => setIsMobileMenuOpen(false)}
          />

          <div className="api-docs-main">
            <TopNavigationBar
              title="API Documentation"
              isMobileMenuOpen={isMobileMenuOpen}
              onToggleMobileMenu={toggleMobileMenu}
            />

            <div className="api-docs-container">
              {/* Header */}
              <header className="api-docs-header">
                <h1 className="api-docs-title">TradeMagic Import API</h1>
                <p className="api-docs-subtitle">
                  Integration guide for partners
                </p>
              </header>

              {/* Overview */}
              <section className="api-docs-section">
                <h2 className="api-docs-section-title">Overview</h2>
                <p className="api-docs-text">
                  TradeMagic supports a session-based import flow that handles
                  large collections without URL length limitations. Instead of
                  encoding CSV data in the URL, you POST the data to our
                  backend, receive a session ID, and redirect users to complete
                  the import.
                </p>

                <div className="api-docs-diagram">
                  <pre>
                    {`┌─────────────────┐     POST /import-session      ┌─────────────────┐
│    Your App     │ ──────────────────────────►   │ TradeMagic API  │
│                 │   { csvData, source }         │                 │
│                 │ ◄──────────────────────────   │                 │
│                 │   { sessionId, importUrl }    │                 │
└────────┬────────┘                               └─────────────────┘
         │
         │  Open browser to importUrl
         ▼
┌─────────────────┐
│ TradeMagic Web  │  User selects condition → previews → logs in → imports
└─────────────────┘`}
                  </pre>
                </div>
              </section>

              {/* API Endpoint */}
              <section className="api-docs-section">
                <h2 className="api-docs-section-title">API Endpoint</h2>
                <div className="api-docs-endpoint">
                  <span className="api-docs-method-badge">POST</span>
                  <span className="api-docs-endpoint-url">
                    https://api.trademagic.gg/api/v1/import-session
                  </span>
                  <p className="api-docs-endpoint-note">
                    Content-Type: application/json | No authentication required
                  </p>
                </div>
              </section>

              {/* Request */}
              <section className="api-docs-section">
                <h2 className="api-docs-section-title">Request</h2>
                <CodeBlock
                  label="JSON"
                  code={`{
  "csvData": "<base64-encoded-csv-string>",
  "source": "your-source-id"
}`}
                />

                <div className="api-docs-table-wrapper">
                  <table className="api-docs-table">
                    <thead>
                      <tr>
                        <th>Field</th>
                        <th>Type</th>
                        <th>Required</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          <code>csvData</code>
                        </td>
                        <td>string</td>
                        <td>
                          <span className="api-docs-required">Yes</span>
                        </td>
                        <td>Base64-encoded CSV data (UTF-8)</td>
                      </tr>
                      <tr>
                        <td>
                          <code>source</code>
                        </td>
                        <td>string</td>
                        <td>
                          <span className="api-docs-required">Yes</span>
                        </td>
                        <td>Partner identifier (provided by TradeMagic during onboarding)</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              {/* CSV Format */}
              <section className="api-docs-section">
                <h2 className="api-docs-section-title">CSV Format</h2>
                <p className="api-docs-text">
                  Generate a <strong>semicolon-delimited</strong> CSV with the
                  following structure:
                </p>

                <CodeBlock
                  label="CSV"
                  code={`export_type;scryfall_uuid;set_code;quantity;foil_quantity;card_name;set_name;cardMarketId;english_card_name;lang;collector_number
card;40c92203-17df-4f10-92c6-ebcc79f01357;dft;1;0;Lifecraft Engine;Aetherdrift;806111;Lifecraft Engine;EN;234
card;f712ac26-dca4-459b-84c1-010597007f60;aer;1;0;Metallic Rebuke;Aether Revolt;294830;Metallic Rebuke;EN;39`}
                />

                <div className="api-docs-subsection">
                  <h3 className="api-docs-subsection-title">Required Columns</h3>
                  <div className="api-docs-table-wrapper">
                    <table className="api-docs-table">
                      <thead>
                        <tr>
                          <th>Column</th>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            <code>export_type</code>
                          </td>
                          <td>
                            Must be <code>card</code> for card entries (other
                            types are skipped)
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <code>scryfall_uuid</code>
                          </td>
                          <td>
                            Scryfall UUID (primary identifier, ensures 100%
                            match rate)
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <code>quantity</code>
                          </td>
                          <td>Number of non-foil copies</td>
                        </tr>
                        <tr>
                          <td>
                            <code>foil_quantity</code>
                          </td>
                          <td>Number of foil copies</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="api-docs-subsection">
                  <h3 className="api-docs-subsection-title">Optional Columns</h3>
                  <div className="api-docs-table-wrapper">
                    <table className="api-docs-table">
                      <thead>
                        <tr>
                          <th>Column</th>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            <code>card_name</code>
                          </td>
                          <td>Card name (used for error reporting)</td>
                        </tr>
                        <tr>
                          <td>
                            <code>set_code</code>
                          </td>
                          <td>Set code (used for error reporting)</td>
                        </tr>
                        <tr>
                          <td>
                            <code>english_card_name</code>
                          </td>
                          <td>English name (preferred over card_name)</td>
                        </tr>
                        <tr>
                          <td>
                            <code>lang</code>
                          </td>
                          <td>Language code (default: "en")</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <p className="api-docs-text">
                  <strong>Important:</strong> Rows with{' '}
                  <code>export_type=deck</code> are automatically skipped. Rows
                  with <code>quantity=0</code> AND <code>foil_quantity=0</code>{' '}
                  are also skipped.
                </p>
              </section>

              {/* Response */}
              <section className="api-docs-section">
                <h2 className="api-docs-section-title">Response</h2>
                <CodeBlock
                  label="JSON (200 OK)"
                  code={`{
  "sessionId": "c90d922d-0221-4df6-a185-a59056bba894",
  "cardCount": 15234,
  "skippedRows": 42,
  "parseWarnings": ["Row 5: Invalid UUID format"],
  "expiresAt": "2026-01-12T00:26:10.504Z",
  "importUrl": "https://trademagic.gg/import/your-source-id?session=c90d922d-0221-4df6-a185-a59056bba894"
}`}
                />

                <div className="api-docs-table-wrapper">
                  <table className="api-docs-table">
                    <thead>
                      <tr>
                        <th>Field</th>
                        <th>Type</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          <code>sessionId</code>
                        </td>
                        <td>string</td>
                        <td>Unique session identifier</td>
                      </tr>
                      <tr>
                        <td>
                          <code>cardCount</code>
                        </td>
                        <td>number</td>
                        <td>Number of valid cards parsed</td>
                      </tr>
                      <tr>
                        <td>
                          <code>skippedRows</code>
                        </td>
                        <td>number</td>
                        <td>Rows skipped (deck entries, invalid data)</td>
                      </tr>
                      <tr>
                        <td>
                          <code>parseWarnings</code>
                        </td>
                        <td>string[]</td>
                        <td>Specific parsing issues (for debugging)</td>
                      </tr>
                      <tr>
                        <td>
                          <code>expiresAt</code>
                        </td>
                        <td>string</td>
                        <td>Session expiration (24 hours from creation)</td>
                      </tr>
                      <tr>
                        <td>
                          <code>importUrl</code>
                        </td>
                        <td>string</td>
                        <td>Redirect user to this URL</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Code Examples */}
              <section className="api-docs-section">
                <h2 className="api-docs-section-title">Code Examples</h2>
                <CodeTabs tabs={codeExamples} />
              </section>

              {/* Limits */}
              <section className="api-docs-section">
                <h2 className="api-docs-section-title">Limits</h2>
                <div className="api-docs-table-wrapper">
                  <table className="api-docs-table">
                    <thead>
                      <tr>
                        <th>Constraint</th>
                        <th>Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Max request body</td>
                        <td>10 MB</td>
                      </tr>
                      <tr>
                        <td>Max cards per import</td>
                        <td>~50,000</td>
                      </tr>
                      <tr>
                        <td>Session expiration</td>
                        <td>24 hours</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Error Responses */}
              <section className="api-docs-section">
                <h2 className="api-docs-section-title">Error Responses</h2>
                <div className="api-docs-table-wrapper">
                  <table className="api-docs-table">
                    <thead>
                      <tr>
                        <th>Status</th>
                        <th>Error</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          <span className="api-docs-status error">400</span>
                        </td>
                        <td>Invalid CSV format</td>
                        <td>CSV parsing failed</td>
                      </tr>
                      <tr>
                        <td>
                          <span className="api-docs-status error">400</span>
                        </td>
                        <td>Invalid base64</td>
                        <td>csvData is not valid base64</td>
                      </tr>
                      <tr>
                        <td>
                          <span className="api-docs-status error">400</span>
                        </td>
                        <td>Missing source</td>
                        <td>source field is required</td>
                      </tr>
                      <tr>
                        <td>
                          <span className="api-docs-status error">413</span>
                        </td>
                        <td>Payload too large</td>
                        <td>CSV exceeds 10MB limit</td>
                      </tr>
                      <tr>
                        <td>
                          <span className="api-docs-status error">500</span>
                        </td>
                        <td>Server error</td>
                        <td>Contact TradeMagic support</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              {/* User Experience */}
              <section className="api-docs-section">
                <h2 className="api-docs-section-title">User Experience</h2>
                <p className="api-docs-text">
                  After redirecting to <code>importUrl</code>, users will:
                </p>
                <ul className="api-docs-list">
                  <li>
                    <strong>Select condition</strong> - Choose default condition
                    for all cards (Near Mint, Lightly Played, etc.)
                  </li>
                  <li>
                    <strong>Preview cards</strong> - See card images and
                    quantities before importing
                  </li>
                  <li>
                    <strong>Sign in / Sign up</strong> - If not already logged
                    in
                  </li>
                  <li>
                    <strong>Import</strong> - Cards added to their TradeMagic
                    collection
                  </li>
                </ul>
              </section>

              {/* Testing */}
              <section className="api-docs-section">
                <h2 className="api-docs-section-title">Testing</h2>

                <div className="api-docs-subsection">
                  <h3 className="api-docs-subsection-title">
                    <span className="api-docs-env-badge staging">Staging</span>
                    Staging Environment
                  </h3>
                  <p className="api-docs-text">
                    API:{' '}
                    <code>
                      https://staging-api.trademagic.gg/api/v1/import-session
                    </code>
                    <br />
                    Frontend:{' '}
                    <code>
                      https://staging.trademagic.gg/import/{'{your-source-id}'}?session=xxx
                    </code>
                  </p>
                </div>

                <div className="api-docs-subsection">
                  <h3 className="api-docs-subsection-title">
                    <span className="api-docs-env-badge production">Prod</span>
                    Production Environment
                  </h3>
                  <p className="api-docs-text">
                    API:{' '}
                    <code>
                      https://api.trademagic.gg/api/v1/import-session
                    </code>
                    <br />
                    Frontend:{' '}
                    <code>
                      https://trademagic.gg/import/{'{your-source-id}'}?session=xxx
                    </code>
                  </p>
                </div>
              </section>

              {/* ============================================================
                   PARTNER DATA API
                   ============================================================ */}

              {/* Partner Data API: intro + shared conventions */}
              <section className="api-docs-section">
                <div className="api-docs-divider">
                  <h2 className="api-docs-divider-title">Partner Data API</h2>
                  <p className="api-docs-divider-text">
                    Read-only daily and hourly data exports for partners. These
                    endpoints are separate from the import flow above and require
                    no authentication.
                  </p>
                </div>

                <p className="api-docs-text">
                  The Partner Data API serves two snapshot datasets: a price
                  export and a wanted-cards export. Both share the same response
                  shape and delivery model, described once here and referenced by
                  each endpoint below.
                </p>

                <div className="api-docs-subsection">
                  <h3 className="api-docs-subsection-title">Response envelope</h3>
                  <p className="api-docs-text">
                    Every response is a JSON object with three top-level fields:
                  </p>
                  <div className="api-docs-table-wrapper">
                    <table className="api-docs-table">
                      <thead>
                        <tr>
                          <th>Field</th>
                          <th>Type</th>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            <code>generatedAt</code>
                          </td>
                          <td>string</td>
                          <td>
                            ISO 8601 timestamp marking when the snapshot was
                            built. Surface this to your users so they understand
                            data recency.
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <code>count</code>
                          </td>
                          <td>number</td>
                          <td>Number of records in the snapshot.</td>
                        </tr>
                        <tr>
                          <td>
                            <code>records</code>
                          </td>
                          <td>array</td>
                          <td>
                            The dataset rows. The per-record shape differs by
                            endpoint and is documented below.
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="api-docs-subsection">
                  <h3 className="api-docs-subsection-title">
                    Snapshot delivery, redirect, and caching
                  </h3>
                  <ul className="api-docs-list">
                    <li>
                      <strong>Snapshot model:</strong> each endpoint serves a
                      pre-built snapshot. Pull it on the documented cadence and
                      store it on your side, then read from your stored copy
                      rather than requesting these bulk routes once per card.
                    </li>
                    <li>
                      <strong>302 to CDN:</strong> the API route responds with a
                      302 redirect to a CDN-served artifact. Standard HTTP clients
                      follow the redirect automatically, so you simply request the
                      route. Integrate against the stable{' '}
                      <code>/api/v1/partner/*</code> route, not the redirect
                      target, which may change.
                    </li>
                    <li>
                      <strong>Format and size:</strong> each artifact is a single
                      JSON document. The price export is gzipped at rest and served
                      with <code>Content-Encoding: gzip</code>, so a standard HTTP
                      client downloads about 34 MB and decompresses it transparently.
                      It is large, so store it rather than fetching it repeatedly.
                    </li>
                    <li>
                      <strong>Caching:</strong> each response carries a{' '}
                      <code>Cache-Control: public, max-age</code> matching its
                      refresh cadence (86400 seconds for prices, 3600 seconds for
                      wanted cards).
                    </li>
                  </ul>
                </div>
              </section>

              {/* Price Data Export */}
              <section className="api-docs-section">
                <h2 className="api-docs-section-title">Price Data Export</h2>
                <div className="api-docs-endpoint">
                  <span className="api-docs-method-badge">GET</span>
                  <span className="api-docs-endpoint-url">
                    https://api.trademagic.gg/api/v1/partner/prices
                  </span>
                  <p className="api-docs-endpoint-note">
                    No authentication required | Regenerated daily
                  </p>
                </div>

                <p className="api-docs-text">
                  Returns a 302 redirect to a daily JSON snapshot of TradeMagic
                  point values, served as a single gzipped artifact from the CDN.
                  There is one record per Scryfall printing-language. Each language
                  of a printing is its own Scryfall ID and its own record.
                </p>

                <div className="api-docs-subsection">
                  <h3 className="api-docs-subsection-title">Record schema</h3>
                  <div className="api-docs-table-wrapper">
                    <table className="api-docs-table">
                      <thead>
                        <tr>
                          <th>Field</th>
                          <th>Type</th>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            <code>scryfallId</code>
                          </td>
                          <td>string</td>
                          <td>
                            Record identity. One printing in one language.
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <code>oracleId</code>
                          </td>
                          <td>string or null</td>
                          <td>
                            Links every printing and language of the same game
                            object.
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <code>setCode</code>
                          </td>
                          <td>string or null</td>
                          <td>Scryfall set code.</td>
                        </tr>
                        <tr>
                          <td>
                            <code>collectorNumber</code>
                          </td>
                          <td>string or null</td>
                          <td>Collector number within the set.</td>
                        </tr>
                        <tr>
                          <td>
                            <code>lang</code>
                          </td>
                          <td>string</td>
                          <td>
                            Scryfall language code (see Languages below).
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <code>name</code>
                          </td>
                          <td>string</td>
                          <td>Card name.</td>
                        </tr>
                        <tr>
                          <td>
                            <code>points</code>
                          </td>
                          <td>number or null</td>
                          <td>
                            Non-foil value, as displayed on TradeMagic.
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <code>pointsFoil</code>
                          </td>
                          <td>number or null</td>
                          <td>Foil value, as displayed on TradeMagic.</td>
                        </tr>
                        <tr>
                          <td>
                            <code>pointsEtched</code>
                          </td>
                          <td>number or null</td>
                          <td>
                            Etched foil value (the Scryfall etched price in
                            points). Present only when the printing has an
                            etched finish.
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <code>tradeMagicUrl</code>
                          </td>
                          <td>string</td>
                          <td>
                            Absolute TradeMagic page URL for this exact
                            printing-language. Always the last field. See The
                            tradeMagicUrl field below.
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="api-docs-subsection">
                  <h3 className="api-docs-subsection-title">
                    The tradeMagicUrl field
                  </h3>
                  <p className="api-docs-text">
                    <code>tradeMagicUrl</code> is the TradeMagic card page for the
                    record&apos;s printing-language. It is appended as the last
                    field of every record, so a reader that maps fields by
                    position is only extended and a reader that maps by name is
                    unaffected. Link to it directly, or store it, or ignore it:
                    the value is stable across snapshots.
                  </p>
                  <p className="api-docs-text">
                    Two shapes appear in one file, and both are correct. English
                    printings with a set code and collector number carry the
                    canonical slug URL,{' '}
                    <code>https://trademagic.gg/cards/{'{name-slug}'}/{'{setCode}'}/{'{collectorNumber}'}</code>
                    , as in the Forest example above. Every other language carries
                    the printing&apos;s UUID URL,{' '}
                    <code>https://trademagic.gg/cards/{'{scryfallId}'}</code>, as in
                    the German Ravnica at War example above, because the slug page
                    resolves to the English printing. A small number of English
                    records with no set code or collector number use the UUID form
                    as well.
                  </p>
                  <p className="api-docs-text">
                    Both forms open the page for that printing. If you would rather
                    build links from the Scryfall ID you already hold,{' '}
                    <code>https://trademagic.gg/cards/scryfall/{'{scryfallId}'}</code>{' '}
                    redirects to the same page and keeps any query parameters you
                    append, such as UTM tags, on the destination.
                  </p>
                  <p className="api-docs-text">
                    To open the page on a specific finish, append{' '}
                    <code>finish=nonfoil</code>, <code>finish=foil</code> or{' '}
                    <code>finish=etched</code> to any card URL. The value is
                    case-insensitive. When the printing exists in that finish the
                    page opens with it selected and its points shown; otherwise the
                    parameter is ignored and the page opens on its default. The key
                    is removed from the address bar once applied, and every other
                    parameter stays. Omit it for the page default. Linking from a
                    foil price, for example:{' '}
                    <code>
                      https://trademagic.gg/cards/scryfall/{'{scryfallId}'}?finish=foil&amp;utm_source=topdecked&amp;utm_medium=partner&amp;utm_campaign=topdecked_integration
                    </code>
                  </p>
                </div>

                <div className="api-docs-subsection">
                  <h3 className="api-docs-subsection-title">
                    Points and the dollar peg
                  </h3>
                  <p className="api-docs-text">
                    Points are TradeMagic's trade value unit. The conversion is
                    fixed and committed: 100 points equals 1 US dollar. A record
                    with <code>points: 250</code> is worth $2.50 non-foil. You can
                    rely on this peg. It will not change.
                  </p>
                </div>

                <div className="api-docs-subsection">
                  <h3 className="api-docs-subsection-title">Foil and non-foil</h3>
                  <p className="api-docs-text">
                    <code>points</code> and <code>pointsFoil</code> are a pair for
                    the same printing-language. They are priced separately, so for
                    most printings <code>points</code> and <code>pointsFoil</code>{' '}
                    differ. Read them together. Do not assume one from the other.
                  </p>
                </div>

                <div className="api-docs-subsection">
                  <h3 className="api-docs-subsection-title">
                    Values are derived and as-displayed
                  </h3>
                  <p className="api-docs-text">
                    The values are the final resolved values shown on the
                    TradeMagic card page. Do your display with these numbers
                    directly. Do not source non-English prices from anywhere else,
                    and do no multiplier math of your own. Most non-English
                    printings do not carry their own raw market price; TradeMagic
                    derives their value from the English printing and a per-language
                    adjustment, and the export already publishes that derived,
                    as-displayed result.
                  </p>
                </div>

                <div className="api-docs-subsection">
                  <h3 className="api-docs-subsection-title">Null semantics</h3>
                  <ul className="api-docs-list">
                    <li>
                      <code>pointsFoil</code> is <code>null</code> when the printing
                      has no foil version. It is never <code>0</code>.
                    </li>
                    <li>
                      <code>points</code> is <code>null</code> only when the row has
                      no derivable non-foil value at all.
                    </li>
                    <li>
                      A row with no derivable value for either finish is omitted from
                      the export.
                    </li>
                  </ul>
                  <p className="api-docs-text">
                    Treat <code>null</code> as "no value available," never as zero.
                  </p>
                </div>

                <div className="api-docs-subsection">
                  <h3 className="api-docs-subsection-title">Languages</h3>
                  <p className="api-docs-text">
                    The export includes the languages TradeMagic prices and
                    displays: <code>en</code>, <code>ja</code>, <code>de</code>,{' '}
                    <code>fr</code>, <code>es</code>, <code>it</code>,{' '}
                    <code>pt</code>, <code>ko</code>, <code>ru</code>,{' '}
                    <code>zhs</code>, <code>zht</code>, and <code>ph</code>. Rare
                    languages outside this set are not exported.
                  </p>
                  <p className="api-docs-text">
                    <code>en</code> (English) and <code>ph</code> (Phyrexian) are
                    self-priced: they carry their own market price and receive no
                    per-language adjustment.
                  </p>
                </div>

                <div className="api-docs-subsection">
                  <h3 className="api-docs-subsection-title">
                    Grouping the same physical printing across languages
                  </h3>
                  <p className="api-docs-text">
                    Two keys with different meanings:
                  </p>
                  <ul className="api-docs-list">
                    <li>
                      <code>oracleId</code> identifies the same game object. It is
                      shared across every set and every printing of a card, so{' '}
                      <code>oracleId</code> alone is NOT the key for "the same
                      physical printing." A single <code>oracleId</code> spans many
                      sets and collector numbers.
                    </li>
                    <li>
                      <code>oracleId</code> + <code>setCode</code> +{' '}
                      <code>collectorNumber</code> identifies the same physical
                      printing across languages. Group on this full tuple to gather
                      the language variants of one printing.
                    </li>
                  </ul>
                  <p className="api-docs-text">
                    Example: War of the Spark printing 28 "Ravnica at War" exists in
                    11 languages. All of its language records share{' '}
                    <code>oracleId = 0012bc78-e69d-4a67-a302-e5fe0dfd4407</code>,{' '}
                    <code>setCode = war</code>, and <code>collectorNumber = 28</code>
                    , and differ only by <code>scryfallId</code> and{' '}
                    <code>lang</code>. Grouping on the tuple gives you the full set
                    of language variants for that one printing.
                  </p>
                </div>

                <div className="api-docs-subsection">
                  <h3 className="api-docs-subsection-title">Example request</h3>
                  <CodeBlock
                    label="cURL"
                    code={`curl -L --compressed https://api.trademagic.gg/api/v1/partner/prices`}
                  />
                </div>

                <div className="api-docs-subsection">
                  <h3 className="api-docs-subsection-title">Example response</h3>
                  <CodeBlock
                    label="JSON (200 OK)"
                    code={`{
  "generatedAt": "2026-06-26T19:21:31.308Z",
  "count": 509002,
  "records": [
    {
      "scryfallId": "fd961194-aae4-469c-bef5-2238ee5f6a28",
      "oracleId": "0012bc78-e69d-4a67-a302-e5fe0dfd4407",
      "setCode": "war",
      "collectorNumber": "28",
      "lang": "de",
      "name": "Ravnica at War",
      "points": 45,
      "pointsFoil": 72,
      "pointsEtched": null,
      "tradeMagicUrl": "https://trademagic.gg/cards/fd961194-aae4-469c-bef5-2238ee5f6a28"
    },
    {
      "scryfallId": "0000419b-0bba-4488-8f7a-6194544ce91e",
      "oracleId": "b34bb2dc-c1af-4d77-b0b3-a0fb342a5fc6",
      "setCode": "blb",
      "collectorNumber": "280",
      "lang": "en",
      "name": "Forest",
      "points": 33,
      "pointsFoil": null,
      "pointsEtched": null,
      "tradeMagicUrl": "https://trademagic.gg/cards/forest/blb/280"
    }
  ]
}`}
                  />
                  <p className="api-docs-text">
                    The <code>records</code> array above is trimmed to two entries
                    for readability. The live response returns the full snapshot.
                  </p>
                </div>

                <div className="api-docs-subsection">
                  <h3 className="api-docs-subsection-title">Size and caching</h3>
                  <p className="api-docs-text">
                    The per-language export is substantially larger than the
                    previous English-only export. Current figures from a full build:
                  </p>
                  <ul className="api-docs-list">
                    <li>
                      Records: about 509,000 (was about 93,000 English-only).
                    </li>
                    <li>
                      Gzipped artifact: about 34 MB (was about 3.5 MB).
                    </li>
                    <li>Raw JSON: about 107 MB.</li>
                  </ul>
                  <p className="api-docs-text">
                    The artifact is gzipped at rest and served with{' '}
                    <code>Content-Encoding: gzip</code>, so a standard HTTP client
                    downloads about 34 MB and decompresses it transparently. It
                    remains a single daily file, cached with{' '}
                    <code>Cache-Control: public, max-age=86400</code>. Pull the daily
                    snapshot once and store it. Do not poll per card.
                  </p>
                </div>
              </section>

              {/* Wanted Cards */}
              <section className="api-docs-section">
                <h2 className="api-docs-section-title">Wanted Cards</h2>
                <div className="api-docs-endpoint">
                  <span className="api-docs-method-badge">GET</span>
                  <span className="api-docs-endpoint-url">
                    https://api.trademagic.gg/api/v1/partner/wanted
                  </span>
                  <p className="api-docs-endpoint-note">
                    No authentication required | Regenerated hourly
                  </p>
                </div>

                <p className="api-docs-text">
                  An hourly snapshot of cards the TradeMagic community is seeking,
                  keyed by oracle card identity. Use it to surface demand signals
                  to your users.
                </p>

                <div className="api-docs-subsection">
                  <h3 className="api-docs-subsection-title">Record schema</h3>
                  <div className="api-docs-table-wrapper">
                    <table className="api-docs-table">
                      <thead>
                        <tr>
                          <th>Field</th>
                          <th>Type</th>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            <code>oracleId</code>
                          </td>
                          <td>string</td>
                          <td>
                            Oracle (card identity) UUID. Identifies the card
                            regardless of printing.
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <code>scryfallId</code>
                          </td>
                          <td>string</td>
                          <td>
                            Scryfall printing UUID for a representative printing.
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <code>name</code>
                          </td>
                          <td>string</td>
                          <td>Card name.</td>
                        </tr>
                        <tr>
                          <td>
                            <code>setCode</code>
                          </td>
                          <td>string</td>
                          <td>Set code for the representative printing.</td>
                        </tr>
                        <tr>
                          <td>
                            <code>wanterCount</code>
                          </td>
                          <td>number</td>
                          <td>
                            Total distinct community members seeking the card. A
                            broad interest signal.
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <code>affordableWanterCount</code>
                          </td>
                          <td>number</td>
                          <td>
                            The subset of wanters who could currently afford the
                            card. A sharper, more actionable signal.
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <code>demandTier</code>
                          </td>
                          <td>string</td>
                          <td>
                            Coarse demand bucket (<code>high</code>,{' '}
                            <code>moderate</code>, or <code>low</code>) derived
                            from <code>wanterCount</code>. It ages more gracefully
                            than a raw number.
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="api-docs-subsection">
                  <h3 className="api-docs-subsection-title">Example request</h3>
                  <CodeBlock
                    label="cURL"
                    code={`curl -L --compressed https://api.trademagic.gg/api/v1/partner/wanted`}
                  />
                </div>

                <div className="api-docs-subsection">
                  <h3 className="api-docs-subsection-title">Example response</h3>
                  <CodeBlock
                    label="JSON (200 OK)"
                    code={`{
  "generatedAt": "2026-06-22T20:35:49.377Z",
  "count": 579,
  "records": [
    {
      "oracleId": "d44a8f89-b9c0-4e8a-9179-baa0c448a3c9",
      "scryfallId": "221a5895-bc21-4f10-b5fc-a4980fde843e",
      "name": "Children of Korlis",
      "setCode": "tsp",
      "wanterCount": 1,
      "affordableWanterCount": 1,
      "demandTier": "low"
    },
    {
      "oracleId": "01fc5bb3-ebd7-4ab4-8aef-2ece1e1d9b7c",
      "scryfallId": "2e5cd12a-2a07-44a8-8eac-de00d26fe9e3",
      "name": "Lotus Vale",
      "setCode": "wth",
      "wanterCount": 1,
      "affordableWanterCount": 0,
      "demandTier": "low"
    }
  ]
}`}
                  />
                  <p className="api-docs-text">
                    The <code>records</code> array above is trimmed to two entries
                    for readability. The live response returns the full snapshot.
                  </p>
                </div>

                <div className="api-docs-subsection">
                  <h3 className="api-docs-subsection-title">Cadence and caching</h3>
                  <p className="api-docs-text">
                    Regenerated hourly. Cached with{' '}
                    <code>Cache-Control: public, max-age=3600</code>. This is a
                    bulk snapshot, so store it and refresh it hourly, then read
                    demand from your stored copy rather than requesting this bulk
                    route once per card.
                  </p>
                </div>

                <div className="api-docs-subsection">
                  <h3 className="api-docs-subsection-title">
                    How to frame this data
                  </h3>
                  <p className="api-docs-text">
                    This is a community-interest signal, not a guarantee of an
                    executable trade. The actual trade happens live on TradeMagic,
                    where real-time matching applies. Surface{' '}
                    <code>generatedAt</code> so users understand recency, and frame
                    callouts as interest or demand rather than a promised live
                    buyer.
                  </p>
                  <p className="api-docs-text">
                    <strong>Affordability basis:</strong>{' '}
                    <code>affordableWanterCount</code> is assessed against the
                    card&apos;s canonical reference price (Near Mint, English,
                    non-foil). It is a strict per-card check and does not model a
                    user wanting many cards at once, a known limitation, so treat
                    it as a strong signal rather than an exact guarantee.
                  </p>
                  <p className="api-docs-text">
                    <strong>Live per-card lookup:</strong> a credentialed live
                    lookup is also available to approved partners for real-time,
                    printing-specific demand checks. It complements this bulk
                    snapshot rather than replacing it: use the bulk export for a
                    broad, stored demand signal, and the live lookup when you need
                    an up-to-the-moment check on a specific printing. If you are
                    interested in access, contact TradeMagic.
                  </p>
                </div>
              </section>

              {/* Cross-dataset join guidance */}
              <section className="api-docs-section">
                <h2 className="api-docs-section-title">
                  Joining Price and Demand Data
                </h2>
                <p className="api-docs-text">
                  The two datasets use different keys on purpose. The price export
                  is keyed at Scryfall printing level (<code>scryfallId</code>),
                  because prices are printing-specific. The wanted export is keyed
                  at oracle card-identity level (<code>oracleId</code>), because
                  demand is identity-level.
                </p>
                <p className="api-docs-text">
                  To combine them, join price data on <code>scryfallId</code> and
                  demand data on <code>oracleId</code>. Do not assume a single
                  shared key across both datasets. The wanted export includes a
                  representative <code>scryfallId</code> per record, which you can
                  use to look up a corresponding price entry.
                </p>
              </section>

              {/* More resources */}
              <section className="api-docs-section">
                <h2 className="api-docs-section-title">More resources</h2>
                <p className="api-docs-text">
                  <Link to="/brand" className="api-docs-inline-link">
                    Brand and style guide
                  </Link>{' '}
                  has our logos, colors, and usage rules for building partner
                  experiences that match the TradeMagic look.
                </p>
              </section>

              {/* Support */}
              <div className="api-docs-support">
                <h2 className="api-docs-support-title">Need Help?</h2>
                <p className="api-docs-support-text">
                  Questions or issues with the integration?
                </p>
                <a
                  href="mailto:support@trademagic.gg"
                  className="api-docs-support-email"
                >
                  support@trademagic.gg
                </a>
              </div>
            </div>
          </div>
        </div>

        <LoginPromptModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          collectionOwnerUsername=""
        />

        <Footer />
      </div>
    </>
  );
};

export default ApiDocumentationPage;
